import * as React from "react";
import { createBrowserRouter, redirect, RouteObject } from "react-router-dom";
import HomePage from "./HomePage";
import ErrorPage from "./errors/ErrorPage";
import { CHILD_ROUTES, INDEX_ROUTES, ROUTES } from "../app/constants";
import ClaimIntakeFormPage from "./ClaimIntakeFormPage";
import MedicalPanelDataPage from "./MedicalPanelDataPage";
import SiteConfigurationPage from "./SiteConfigurationPage";
import { loader as homePageLoader } from "./loaders/HomePageLoader";
import { loader as adminPagesLoader } from "./loaders/AdminPagesLoader";
import { loader as claimIntakeFormLoader } from "./loaders/ClaimIntakeFormPageLoader";
import { loader as claimsHistoryLoader } from "./loaders/ClaimsHistoryPageLoader";
import { loader as medicalPanelLoader } from "./loaders/MedicalPanelPageLoader";
import { loader as cargoPagesLoader } from "./loaders/CargoPagesLoader";
import { loader as iplPagesLoader } from "./loaders/IPLPagesLoader";
import { loader as checkRolesLoader } from "./loaders/CheckRolesLoader";
import { loader as workersCompensationPagesLoader } from "./loaders/WorkersCompensationPagesLoader";
import { loader as legacyWcLoader } from "./loaders/LegacyWcLoader";
import { loader as landingPageLoader } from "./loaders/LandingPageLoader";

import ClaimsHistoryPage from "./ClaimsHistoryPage";
import MySubmittedClaimsPage from "./MySubmittedClaimsPage";
import { User } from "../auth/types/interfaces";
import WorkersCompensationDocumentsPage from "./WorkersCompensationDocumentsPage";
import DocumentsManagementPage from "./DocumentsManagementPage";
import CargoClaimIntakePage from "./cargo/CargoClaimIntakePage";
import CargoJsonFormsPage from "./cargo/CargoJsonFormsPage";
import { JSON_FORMS_ROUTES } from "src/jsonForms/constants";
import { getFeatureFlag } from "src/app/featureFlags";
import IPLClaimIntakePage from "./ipl/IPLClaimIntakePage";
import { LandingPage } from "./LandingPage";

// These routes are to support old workers compensation routes
// with the inclusion of other lines of business the root route for
// workers compensation changed to /workers-compensation
// so we need to redirect people to the new URL in case someone
// uses the old URLs
const workersCompensationLegacyRoutes = (): RouteObject[] => [
  {
    path: CHILD_ROUTES.MY_SUBMITTED_CLAIMS,
    loader: () => legacyWcLoader(),
  },
  {
    path: CHILD_ROUTES.MEDICAL_PANEL_DATA,
    loader: () => legacyWcLoader(),
  },
  {
    path: CHILD_ROUTES.CLAIMS_HISTORY,
    loader: () => legacyWcLoader(),
  },
  {
    path: CHILD_ROUTES.WORKERS_COMPENSATION_DOCUMENTS,
    loader: () => legacyWcLoader(),
  },
  {
    path: CHILD_ROUTES.LEGACY_WORKERS_COMPENSATION_DOCUMENTS,
    loader: () => legacyWcLoader(),
  },
];
const workersCompensationRoutes = (user: User): RouteObject[] => [
  // Index route to redirect /workers-compensation to /workers-compensation/claim-intake-form
  {
    path: INDEX_ROUTES.WORKERS_COMPENSATION,
    index: true,
    loader: () => redirect(ROUTES.WC_CLAIM_INTAKE_FORM),
  },
  {
    path: ROUTES.WC_CLAIM_INTAKE_FORM,
    element: <ClaimIntakeFormPage />,
    loader: () => claimIntakeFormLoader(user),
  },
  {
    path: ROUTES.MY_SUBMITTED_CLAIMS,
    element: <MySubmittedClaimsPage />,
    loader: () => claimsHistoryLoader(user),
  },
  {
    path: ROUTES.MEDICAL_PANEL_DATA,
    element: <MedicalPanelDataPage />,
    loader: () => medicalPanelLoader(),
  },
  {
    path: ROUTES.CLAIMS_HISTORY,
    element: <ClaimsHistoryPage />,
    loader: () => claimsHistoryLoader(user),
  },
  {
    path: ROUTES.WORKERS_COMPENSATION_DOCUMENTS,
    element: <WorkersCompensationDocumentsPage />,
  },
];
/**
 * Loader that gates JSON Forms routes behind the enableCargoJsonForms feature flag.
 * Redirects to the legacy cargo form when the flag is disabled.
 */
const cargoJsonFormsLoader = async () => {
  const isEnabled = await getFeatureFlag("enableCargoJsonForms");
  if (!isEnabled) {
    return redirect(ROUTES.CARGO_CLAIM_INTAKE_FORM);
  }
  return null;
};

const cargoRoutes = (user: User): RouteObject[] => [
  // Index route to redirect /cargo to /cargo/claim-intake-form
  {
    path: INDEX_ROUTES.CARGO,
    index: true,
    loader: () => redirect(ROUTES.CARGO_CLAIM_INTAKE_FORM),
  },
  {
    path: ROUTES.CARGO_CLAIM_INTAKE_FORM,
    element: <CargoClaimIntakePage />,
  },
  {
    path: `${ROUTES.CARGO_CONTINUE_FORM}`,
    element: <CargoClaimIntakePage />,
  },
  {
    path: ROUTES.CARGO_CLAIMS_HISTORY,
    element: <ClaimsHistoryPage />,
    loader: () => claimsHistoryLoader(user),
  },
  {
    path: JSON_FORMS_ROUTES.CARGO_CLAIM_INTAKE_FORM,
    element: <CargoJsonFormsPage />,
    loader: cargoJsonFormsLoader,
  },
  {
    path: JSON_FORMS_ROUTES.CARGO_CONTINUE_FORM,
    element: <CargoJsonFormsPage />,
    loader: cargoJsonFormsLoader,
  },
];
const iplRoutes = (user: User): RouteObject[] => [
  // Index route to redirect /ipl to /ipl/claim-intake-form
  {
    path: INDEX_ROUTES.IPL,
    index: true,
    loader: () => redirect(ROUTES.IPL_CLAIM_INTAKE_FORM),
  },
  {
    path: ROUTES.IPL_CLAIM_INTAKE_FORM,
    element: <IPLClaimIntakePage />,
  },
  {
    path: ROUTES.IPL_CLAIMS_HISTORY,
    element: <ClaimsHistoryPage />,
    loader: () => claimsHistoryLoader(user),
  },
];
const adminRoutes = (): RouteObject[] => [
  {
    path: ROUTES.SITE_CONFIGURATION,
    element: <SiteConfigurationPage />,
  },
  {
    path: ROUTES.DOCUMENTS_MANAGEMENT,
    element: <DocumentsManagementPage />,
  },
];
export const createAppRouter = (user: User) => {
  return createBrowserRouter([
    {
      path: "/",
      element: <HomePage />,
      errorElement: <ErrorPage></ErrorPage>,
      loader: () => homePageLoader(),
      children: [
        {
          path: "/",
          index: true,
          loader: () => checkRolesLoader(user),
        },
        {
          path: CHILD_ROUTES.WC_CLAIM_INTAKE_FORM,
          loader: () => checkRolesLoader(user),
        },
        {
          path: INDEX_ROUTES.ADMIN,
          loader: () => adminPagesLoader(user),
          children: adminRoutes(),
        },
        {
          path: INDEX_ROUTES.WORKERS_COMPENSATION,
          loader: () => workersCompensationPagesLoader(user),
          children: workersCompensationRoutes(user),
        },
        {
          path: INDEX_ROUTES.CARGO,
          loader: () => cargoPagesLoader(user),
          children: cargoRoutes(user),
        },
        {
          path: INDEX_ROUTES.IPL,
          loader: () => iplPagesLoader(user),
          children: iplRoutes(user),
        },
        {
          path: ROUTES.LANDING_PAGE,
          element: <LandingPage />,
          loader: () => landingPageLoader(user),
        },
        ...workersCompensationLegacyRoutes(),
      ],
    },
  ]);
};
